import { useQuery } from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * The portal side of the SPA (docs/portal.md). Its own client on purpose: no
 * mock-auth headers, no internal session — a portal user is a different kind of
 * person, signed in with a different cookie that this code never sees (httpOnly).
 *
 * Nothing here decides access. The API re-reads the account on every request.
 */

export type PortalModule = 'GROUNDWORK_REPORT' | 'DELIVERY_PLANNING';

export interface PortalMe {
  portalUserId: string;
  email: string;
  name: string;
  partyKind: string;
  partyCode: string;
  partyName: string;
  modules: PortalModule[];
  /** Signed in with a recovery code: link a new phone before anything else. */
  mustRelink: boolean;
  recoveryCodesLeft: number;
  passwordSetAt: string | null;
}

async function parseError(res: Response): Promise<ApiError> {
  let code = 'API_ERROR';
  let message = `Request failed with status ${res.status}`;
  let body: unknown;
  try {
    body = await res.json();
    const json = body as { error?: { code?: string; message?: string } };
    if (json.error?.code) code = json.error.code;
    if (json.error?.message) message = json.error.message;
  } catch {
    // non-JSON error body — keep defaults
  }
  return new ApiError(res.status, code, message, body);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(new URL(path, window.location.origin).toString(), {
    method,
    credentials: 'same-origin',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const portalApi = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
};

/**
 * POST multipart with upload progress. XHR rather than fetch: a phone on a
 * building site uploading ten photos wants to see that something is happening.
 */
export function postForm<T>(
  path: string,
  form: FormData,
  onProgress?: (fraction: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path);
    xhr.withCredentials = true;
    xhr.responseType = 'json';
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new ApiError(0, 'NETWORK_ERROR', 'Network error'));
    xhr.onload = () => {
      const body = xhr.response as { error?: { code?: string; message?: string } } | null;
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as T);
      else
        reject(
          new ApiError(
            xhr.status,
            body?.error?.code ?? 'API_ERROR',
            body?.error?.message ?? `Upload failed with status ${xhr.status}`,
            body,
          ),
        );
    };
    xhr.send(form);
  });
}

/** Open a PDF the API streams, in a new tab — via a blob, so it rides on the cookie. */
export async function openPortalPdf(path: string): Promise<void> {
  const tab = window.open('', '_blank');
  const res = await fetch(path, { credentials: 'same-origin' });
  if (!res.ok) {
    tab?.close();
    throw await parseError(res);
  }
  const url = URL.createObjectURL(await res.blob());
  if (tab) tab.location.href = url;
  else window.location.href = url;
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const portalSessionQueryKey = ['portal-session'] as const;

export async function fetchPortalSession(): Promise<PortalMe | null> {
  try {
    return await portalApi.get<PortalMe>('/api/v1/portal/auth/me');
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) return null;
    throw err;
  }
}

export function usePortalSession() {
  return useQuery({
    queryKey: portalSessionQueryKey,
    queryFn: fetchPortalSession,
    staleTime: 60_000,
  });
}

export async function portalLogout(): Promise<void> {
  try {
    await portalApi.post('/api/v1/portal/auth/logout');
  } finally {
    window.location.href = '/login';
  }
}

/** Where a fresh portal session starts. */
export function portalHome(me: PortalMe): string {
  if (me.mustRelink) return '/portal/new-phone';
  if (me.modules.includes('GROUNDWORK_REPORT')) return '/portal/groundwork';
  return '/portal/account';
}

/**
 * The API's error codes → i18n keys. The API's own messages are Dutch already,
 * but the screen speaks two languages and says the same thing in both.
 */
export function portalErrorKey(err: unknown): string {
  if (!(err instanceof ApiError)) return 'portal.error.generic';
  switch (err.code) {
    case 'LOGIN_FAILED':
      return 'portal.error.loginFailed';
    case 'LOGIN_LOCKED':
      return 'portal.error.locked';
    case 'CODE_WRONG':
      return 'portal.error.codeWrong';
    case 'SIGN_IN_AGAIN':
      return 'portal.error.signInAgain';
    case 'LINK_INVALID':
      return 'portal.error.linkInvalid';
    case 'PASSWORD_WRONG':
      return 'portal.error.passwordWrong';
    case 'SECOND_FACTOR_OFF':
      return 'portal.error.unavailable';
    case 'NETWORK_ERROR':
      return 'portal.error.network';
    case 'VALIDATION_ERROR': {
      const detail = (err.body as { error?: { details?: { code?: string }[] } } | undefined)?.error
        ?.details?.[0]?.code;
      if (
        detail &&
        ['too_short', 'too_long', 'same_as_email', 'same_as_current'].includes(detail)
      ) {
        return `portal.password.rule.${detail}`;
      }
      return 'portal.error.generic';
    }
    default:
      return err.status === 429 ? 'portal.error.tooMany' : 'portal.error.generic';
  }
}

/** Mirrors the API policy (portal.service.ts) — the courtesy, not the check. */
export const PASSWORD_MIN = 10;
export function newPasswordProblem(password: string, email: string): string | null {
  if (password.length < PASSWORD_MIN) return 'too_short';
  if (password.length > 128) return 'too_long';
  const p = password.trim().toLowerCase();
  const e = email.toLowerCase();
  if (p === e || p === e.split('@')[0]) return 'same_as_email';
  return null;
}
